import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ButtonEdit } from '../../../components/Buttons';
import { FormContainer, FormInput, FormRow, Label } from '../../../components/form';
import { getFormErrorMessage } from '../../../utils/errors';
import { LeadershipTable } from './LeadershipTable';
import { LeadershipFormDialog } from './LeadershipFormDialog';
import {
    RHFMultiSelect,
    RHFInputNumber,
    RHFYearCalendar,
    RHFCheckbox
} from '../../../components/rhf';
import { MAX_TERM_LENGTH } from '../../../constants/constants';

export const TermEditForm = ({
    term,
    politicalEntities,
    members,
    onSubmit
}) => {
    const [processing, setProcessing] = useState(false);
    const [leadership, setLeadership] = useState(
        term.members.filter(
            member => Array.isArray(member.leadership_roles) && member.leadership_roles.length > 0
        )
    );
    const [leadershipMember, setLeadershipMember] = useState(null);
    const [showLeadershipFormDialog, setShowLeadershipFormDialog] = useState(false);

    const defaultValues = {
        year_from: new Date(term.year_from, 0, 1),
        year_to: new Date(term.year_to, 0, 1),
        order: term.order,
        coalition: term.coalition,
        leadership: leadership.map(member => ({
            ...member,
            leadership_roles: member.leadership_roles.map(role => ({
                ...role,
                date_from: role.date_from ? new Date(role.date_from) : null,
                date_to: role.date_to ? new Date(role.date_to) : null
            }))
        })),
        dataset_complete: term.dataset_complete
    };

    const {
        control,
        formState: { errors, isSubmitting },
        handleSubmit
    } = useForm({
        disabled: processing,
        defaultValues: defaultValues
    });

    useEffect(() => {
        setProcessing(isSubmitting);
    }, [isSubmitting]);

    const handleClickAddRoles = () => {
        setLeadershipMember(null);
        setShowLeadershipFormDialog(true);
    };

    const handleClickEditRoles = (member) => {
        setLeadershipMember(member);
        setShowLeadershipFormDialog(true);
    };

    const hideLeadershipDialog = () => {
        setLeadershipMember(null);
        setShowLeadershipFormDialog(false);
    };

    const handleSubmitLeadershipForm = (data) => {
        let updated = [...leadership];
        const exists = updated.some((m) => m.id === data.member);
        if (exists) {
            updated = updated.map((m) =>
                m.id === data.member
                    ? { ...m, leadership_roles: data.leadership_roles }
                    : m
            );
        } else {
            const member = term.members.find((m) => m.id === data.member);
            updated.push({
                id: member.id,
                first_name: member.first_name,
                last_name: member.last_name,
                leadership_roles: data.leadership_roles,
            });
        }
        setLeadership(updated.filter((m) => m.leadership_roles?.length > 0));
        hideLeadershipDialog();
    };

    return (
        <>
            <form onSubmit={handleSubmit((data) => onSubmit(data, leadership))}>
                <FormContainer>
                    <FormRow>
                        <FormInput>
                            <Label htmlFor='year_from' text='Začátek' required />
                            <RHFYearCalendar
                                control={control}
                                name='year_from'
                                rules={{ required: 'Zadejte začátek funkčního období' }}
                                placeholder='Rok od'
                            />
                            {getFormErrorMessage(errors, 'year_from')}
                        </FormInput>
                        <FormInput>
                            <Label htmlFor='year_to' text='Konec' required />
                            <RHFYearCalendar
                                control={control}
                                name='year_to'
                                rules={{
                                    required: 'Zadejte konec funkčního období',
                                    validate: {
                                        order: (value, formValues) => {
                                            const start = formValues.year_from;
                                            const end = value;
                                            if (!start || !end) return;
                                            if (start.getFullYear() > end.getFullYear()) {
                                                return 'Konec funkčního období musí být později než začátek';
                                            }
                                        },
                                        duration: (value, formValues) => {
                                            const start = formValues.year_from;
                                            const end = value;
                                            if (!start || !end) return;
                                            const termLength = end.getFullYear() - start.getFullYear();
                                            if (termLength > MAX_TERM_LENGTH) {
                                                return `Funkční období může trvat maximálně ${MAX_TERM_LENGTH} roky`;
                                            }
                                        }
                                    }
                                }}
                                placeholder='Rok do'
                            />
                            {getFormErrorMessage(errors, 'year_to')}
                        </FormInput>
                        <FormInput type='checkbox'>
                            <Label
                                htmlFor='dataset_complete'
                                text='Dataset kompletní'
                                info={
                                    <>
                                        <p>
                                            Značí, zda jsou v systému zadána všechna
                                            dosavadní hlasování tohoto funkčního období.
                                        </p>
                                    </>
                                }
                            />
                            <RHFCheckbox control={control} name='dataset_complete' />
                        </FormInput>
                    </FormRow>
                    <FormRow>
                        <FormInput>
                            <Label htmlFor='order' text='Pořadí' />
                            <RHFInputNumber control={control} name='order' disabled />
                        </FormInput>
                        <FormInput>
                            <Label htmlFor='coalition' text='Koalice' />
                            <RHFMultiSelect
                                control={control}
                                name='coalition'
                                placeholder='Koaliční subjekty'
                                inputProps={{
                                    options: politicalEntities,
                                    optionLabel: 'abbreviation',
                                    optionValue: 'id'
                                }}
                            />
                        </FormInput>
                    </FormRow>
                    <FormRow>
                        <Label text='Vedení' info='Vedoucí pozice orgánu během tohoto funkčního období.' />
                    </FormRow>
                    <FormRow>
                        <LeadershipTable
                            control={control}
                            errors={errors}
                            leadership={leadership}
                            disabled={processing}
                            onClickAddRoles={handleClickAddRoles}
                            onClickEditRoles={handleClickEditRoles}
                        />
                    </FormRow>
                    <FormRow type='button'>
                        <ButtonEdit type='submit' label='Uložit úpravy' disabled={processing} loading={processing} />
                    </FormRow>
                </FormContainer>
            </form>

            <LeadershipFormDialog
                members={members}
                leadership={leadership}
                editMember={leadershipMember}
                isVisible={showLeadershipFormDialog}
                onHide={hideLeadershipDialog}
                onSubmit={handleSubmitLeadershipForm}
            />
        </>
    );
};
